import chalk from 'chalk';
import { readFileSync, existsSync } from 'node:fs';
import { join } from 'node:path';
import {
  extractFrontmatter,
  getChannel,
  type CharterFrontmatter,
  type ToolsFrontmatter,
} from '@augmented/core';
import { error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { api } from '../lib/api-client.js';
import { getApiKey } from '../lib/config.js';

interface AgentShowOptions {
  configDir: string;
  allChannels?: boolean;
}

export async function agentShowCommand(
  codeName: string,
  opts: AgentShowOptions,
): Promise<void> {
  const json = isJsonMode();
  // ENG-4418: both frameworks collapse to <configDir>/<codeName>/provision/
  // post-migration. This command doesn't go through the adapter, so on a
  // host where the manager hasn't polled yet the legacy
  // <codeName>/claudecode/provision/ may still be the only tree present —
  // prefer the unified path, fall back to legacy so agent show doesn't
  // report 'not found' during the migration window.
  const unifiedDir = join(opts.configDir, codeName, 'provision');
  const legacyDir = join(opts.configDir, codeName, 'claudecode', 'provision');
  const agentDir = existsSync(unifiedDir) ? unifiedDir : legacyDir;
  const hasLocalConfig = existsSync(agentDir);

  // ── Try API first for channel data ─────────────────────────────────────

  type ApiChannelConfig = {
    channel_id: string;
    config: Record<string, unknown>;
    status: string;
  };

  let apiChannels: ApiChannelConfig[] | null = null;
  let apiAgent: Record<string, unknown> | null = null;

  if (getApiKey()) {
    try {
      const data = await api.get<{
        agent: Record<string, unknown>;
        channel_configs: Record<string, { config: Record<string, unknown>; status: string }>;
      }>(`/agents/${encodeURIComponent(codeName)}/provision-data`);

      apiAgent = data.agent ?? null;

      if (data.channel_configs) {
        apiChannels = Object.entries(data.channel_configs).map(
          ([channelId, val]) => ({
            channel_id: channelId,
            config: val.config,
            status: val.status,
          }),
        );
      }
    } catch {
      // API not available or agent not found — continue with local data
    }
  }

  // If no local config and no API data, agent doesn't exist anywhere
  if (!hasLocalConfig && !apiAgent) {
    if (json) {
      jsonOutput({ ok: false, error: `Agent '${codeName}' not found` });
    } else {
      error(`Agent '${codeName}' not found`);
    }
    process.exitCode = 1;
    return;
  }

  // ── Read local files (best-effort) ────────────────────────────────────

  let charter: CharterFrontmatter | null = null;
  let tools: ToolsFrontmatter | null = null;
  let agentState: {
    charterVersion: string;
    toolsVersion: string;
    lastProvisionAt: string | null;
    lastDriftCheckAt: string | null;
  } | null = null;

  if (hasLocalConfig) {
    // CHARTER.md
    const charterPath = join(agentDir, 'CHARTER.md');
    if (existsSync(charterPath)) {
      const raw = readFileSync(charterPath, 'utf-8');
      const parsed = extractFrontmatter(raw);
      if (parsed.frontmatter) {
        charter = parsed.frontmatter as unknown as CharterFrontmatter;
      }
    }

    // TOOLS.md
    const toolsPath = join(agentDir, 'TOOLS.md');
    if (existsSync(toolsPath)) {
      const raw = readFileSync(toolsPath, 'utf-8');
      const parsed = extractFrontmatter(raw);
      if (parsed.frontmatter) {
        tools = parsed.frontmatter as unknown as ToolsFrontmatter;
      }
    }

    // manager-state.json
    const statePath = join(opts.configDir, 'manager-state.json');
    if (existsSync(statePath)) {
      try {
        const raw = readFileSync(statePath, 'utf-8');
        const state = JSON.parse(raw) as {
          agents: Array<{
            codeName: string;
            charterVersion: string;
            toolsVersion: string;
            lastProvisionAt: string | null;
            lastDriftCheckAt: string | null;
          }>;
        };
        agentState = state.agents?.find((a) => a.codeName === codeName) ?? null;
      } catch {
        // ignore
      }
    }
  }

  // ── Resolve channel data ────────────────────────────────────────────────
  // The webapp/API is the source of truth for channel configuration.

  type ChannelRow = {
    id: string;
    name: string;
    tier: string;
    enabled: boolean;
    status: string;
    e2e: string;
  };

  const channelRows: ChannelRow[] = [];

  if (apiChannels && apiChannels.length > 0) {
    // Use API channel configs (source of truth)
    for (const ch of apiChannels) {
      const def = getChannel(ch.channel_id);
      channelRows.push({
        id: ch.channel_id,
        name: def?.name ?? ch.channel_id,
        tier: def?.securityTier ?? 'unknown',
        enabled: true,
        status: ch.status,
        e2e: def ? String(def.e2eEncrypted) : '—',
      });
    }
  }

  // ── JSON output ─────────────────────────────────────────────────────────

  if (json) {
    const filtered = opts.allChannels ? channelRows : channelRows.filter((c) => c.enabled);
    jsonOutput({
      ok: true,
      agent: {
        code_name: codeName,
        display_name: (apiAgent?.display_name as string) ?? charter?.display_name ?? null,
        agent_id: (apiAgent?.agent_id as string) ?? charter?.agent_id ?? null,
        environment: (apiAgent?.environment as string) ?? charter?.environment ?? null,
        risk_tier: (apiAgent?.risk_tier as string) ?? charter?.risk_tier ?? null,
        owner: charter?.owner ?? null,
        logging_mode: charter?.logging_mode ?? null,
        budget: charter?.budget ?? null,
        limits: charter?.limits ?? null,
      },
      versions: {
        charter: agentState?.charterVersion ?? charter?.version ?? null,
        tools: agentState?.toolsVersion ?? tools?.version ?? null,
        last_provision: agentState?.lastProvisionAt ?? null,
        last_drift_check: agentState?.lastDriftCheckAt ?? null,
      },
      tools: {
        enforcement_mode: tools?.enforcement_mode ?? null,
        global_controls: tools?.global_controls ?? null,
        count: tools?.tools?.length ?? 0,
        items: tools?.tools ?? [],
      },
      channels: filtered,
    });
    return;
  }

  // ── Human output ────────────────────────────────────────────────────────

  const displayName = (apiAgent?.display_name as string) ?? charter?.display_name ?? codeName;
  console.log(chalk.bold(`\nAgent: ${codeName}`) + (displayName !== codeName ? ` (${displayName})` : '') + '\n');

  if (charter || apiAgent) {
    const agentId = (apiAgent?.agent_id as string) ?? charter?.agent_id;
    const environment = (apiAgent?.environment as string) ?? charter?.environment;
    const riskTier = (apiAgent?.risk_tier as string) ?? charter?.risk_tier;

    info(`Agent ID:     ${agentId ?? '—'}`);
    info(`Environment:  ${environment ?? '—'}`);
    info(`Risk Tier:    ${riskTier ?? '—'}`);
    info(`Owner:        ${charter?.owner?.name ?? '—'}`);

    const charterVer = agentState?.charterVersion ?? charter?.version;
    const toolsVer = agentState?.toolsVersion ?? tools?.version ?? '—';
    const provTs = agentState?.lastProvisionAt
      ? formatTimestamp(agentState.lastProvisionAt)
      : null;

    if (charterVer) {
      info(`Charter:      v${charterVer}${provTs ? ` (provisioned ${provTs})` : ''}`);
    }
    if (toolsVer !== '—') {
      info(`Tools:        v${toolsVer}${provTs ? ` (provisioned ${provTs})` : ''}`);
    }

    // Budget
    const b = charter?.budget;
    if (b) {
      const budgetStr = `${b.limit.toLocaleString()} ${b.type} / ${b.window} (${b.enforcement ?? 'block'})`;
      info(`Budget:       ${budgetStr}`);
    }
  } else {
    info('No agent metadata available');
  }

  // ── Channels table ──────────────────────────────────────────────────────

  const filtered = opts.allChannels ? channelRows : channelRows.filter((c) => c.enabled);

  console.log(chalk.bold('\nChannels:'));

  if (filtered.length === 0) {
    info('(none enabled)');
  } else {
    table(
      ['Channel', 'Name', 'Tier', 'Status', 'E2E'],
      filtered.map((c) => [
        c.id,
        c.name,
        c.tier,
        c.status,
        c.e2e,
      ]),
    );
  }

  // ── Tools ───────────────────────────────────────────────────────────────

  if (tools) {
    console.log(chalk.bold('\nTools:'));

    if (tools.tools.length === 0) {
      info('(none configured)');
    } else {
      table(
        ['ID', 'Name', 'Type', 'Access', 'Enforcement'],
        tools.tools.map((t) => [t.id, t.name, t.type, t.access, t.enforcement]),
      );
    }

    // Global controls
    if (tools.global_controls) {
      const gc = tools.global_controls;
      console.log(chalk.bold('\nGlobal Controls:'));
      info(`Network:   ${gc.default_network_policy === 'deny' ? 'deny-by-default' : 'allow-by-default'}`);
      info(`Timeout:   ${gc.default_timeout_ms}ms`);
      info(`Rate:      ${gc.default_rate_limit_rpm} rpm`);
      info(`Retries:   ${gc.default_retries}`);
    }
  }

  console.log();
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
